import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { ElementHelper } from '@alchemiesolns/elements';
import type { MolecularGraphJSON, AtomJSON, BondJSON } from '@alchemiesolns/feature-extractor';

export type ConversionResult = {
    json: MolecularGraphJSON;
    /** json.a[i] corresponds to workspace atom indexToAtomId[i] */
    indexToAtomId: string[];
    /** json.b[i] corresponds to workspace bond indexToBondId[i] */
    indexToBondId: string[];
};

export type ConversionOptions = {
    /**
     * When true, keep ALL hydrogens as explicit atoms (don't collapse hidden H to implicit counts).
     * Use this for navigation features where accessibility requires all hydrogens to be listed.
     * Default: false (hidden H collapsed to implicit counts for alt-text/API token optimization).
     */
    keepAllHydrogensExplicit?: boolean;
};

/**
 * Convert a MolecularWorkspace into MolecularGraphJSON format for the feature-extractor.
 *
 * By default, hidden hydrogen atoms are collapsed into implicit H counts on their parent atoms
 * rather than appearing in the atoms array. Visible hydrogens remain explicit.
 *
 * Set `options.keepAllHydrogensExplicit` to true to keep ALL hydrogens as explicit atoms,
 * which is needed for navigation features where accessibility requires all hydrogens to be listed.
 */
export function toMolecularGraphJSON(workspace: MolecularWorkspace, options?: ConversionOptions): ConversionResult {
    const { keepAllHydrogensExplicit = false } = options ?? {};
    const indexToAtomId: string[] = [];
    const indexToBondId: string[] = [];

    // Map from workspace atom ID → index in output atoms array (for included atoms only)
    const atomIdToIndex = new Map<string, number>();

    // Track implicit H counts per parent atom ID
    const implicitHByParent = new Map<string, number>();

    // Set of excluded atom IDs (hidden hydrogens)
    const excludedAtomIds = new Set<string>();

    // --- Pass 1: classify atoms ---
    for (const atomId of workspace.getAtomIds()) {
        const atom = workspace.getAtom(atomId);
        if (!atom) continue;

        const vis = workspace.getVisibility(atomId);
        const isHydrogen = atom.symbol === 'H';
        const isHidden = vis === VisibilityFlag.Hidden;

        if (isHydrogen && isHidden && !keepAllHydrogensExplicit) {
            // Hidden H → find parent atom via bonds and count as implicit
            // (Skip this when keepAllHydrogensExplicit is true - for navigation/accessibility)
            excludedAtomIds.add(atomId);

            for (const bond of workspace.getAllBonds()) {
                let parentId: string | null = null;
                if (bond.a1.id === atomId) parentId = bond.a2.id;
                else if (bond.a2.id === atomId) parentId = bond.a1.id;

                if (parentId) {
                    implicitHByParent.set(parentId, (implicitHByParent.get(parentId) ?? 0) + 1);
                }
            }
        } else {
            // Include this atom in the output array
            const index = indexToAtomId.length;
            atomIdToIndex.set(atomId, index);
            indexToAtomId.push(atomId);
        }
    }

    // --- Pass 2: build atoms array ---
    const atoms: AtomJSON[] = indexToAtomId.map(atomId => {
        const atom = workspace.getAtom(atomId)!;
        const implicitH = implicitHByParent.get(atomId) ?? 0;

        const aj: AtomJSON = {
            s: atom.symbol,
            n: ElementHelper.getAtomicNumberBySymbol(atom.symbol),
            p: [atom.x, atom.y, 0],
        };

        if (atom.charge !== 0) aj.q = atom.charge;
        if (atom.lonePairs > 0) aj.lp = atom.lonePairs;
        if (implicitH > 0) aj.h = implicitH;
        if (atom.radicals > 0) aj.r = atom.radicals;
        // PIPH-269: surface R/S so the alt-text fact block can label stereocenters.
        if (atom.chirality) aj.c = atom.chirality;

        return aj;
    });

    // --- Pass 3: build bonds array ---
    const bondsjson: BondJSON[] = [];

    for (const bond of workspace.getAllBonds()) {
        const bondId = bond.id;
        // Skip bonds where either atom was excluded (hidden H)
        if (excludedAtomIds.has(bond.a1.id) || excludedAtomIds.has(bond.a2.id)) continue;

        const aIdx = atomIdToIndex.get(bond.a1.id);
        const bIdx = atomIdToIndex.get(bond.a2.id);
        if (aIdx === undefined || bIdx === undefined) continue;

        const bj: BondJSON = {
            a: aIdx,
            b: bIdx,
            o: bond.order,
        };
        if (bond.bondType !== 'l') bj.t = bond.bondType;
        // PIPH-269: surface E/Z geometry so the alt-text fact block can label alkenes.
        if (bond.isomerConfig) bj.i = bond.isomerConfig;

        bondsjson.push(bj);
        indexToBondId.push(bondId);
    }

    return {
        json: { a: atoms, b: bondsjson },
        indexToAtomId,
        indexToBondId,
    };
}
